import { useState, useCallback } from 'react';
import { nanoid } from 'nanoid';
import { VideoItem } from '../types';
import { processVideoFile } from '../services/video-processor';

interface UseVideoUploadProps {
  currentCount: number;
  onUpload: (items: VideoItem[]) => void;
  updateVideoStatus: (id: string, updates: Partial<VideoItem>) => void;
  onError: (msg: string) => void;
}

export const useVideoUpload = ({ currentCount, onUpload, updateVideoStatus, onError }: UseVideoUploadProps) => {
  const [isDragActive, setIsDragActive] = useState(false);

  const processFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      if (currentCount + files.length > 10) {
        onError('You can only upload a maximum of 10 videos.');
        return;
      }

      // 1. Create Initial Items (Loading State)
      const initialItems: VideoItem[] = [];
      const filesToProcess: { id: string; file: File }[] = [];

      files.forEach((file) => {
        if (!file.type.startsWith('video/')) {
          onError(`File "${file.name}" is not a valid video.`);
          return;
        }

        const id = nanoid();
        initialItems.push({
          id,
          file, // we may need to remove it after frames are extracted, if biz ask to increase batch size
          fileName: file.name,
          status: 'EXTRACTING',
          extractionProgress: 0,
          timestamp: Date.now()
        });
        filesToProcess.push({ id, file });
      });

      if (initialItems.length > 0) {
        onUpload(initialItems);
      }

      for (const { id, file } of filesToProcess) {
        try {
          const data = await processVideoFile(file);

          updateVideoStatus(id, {
            status: 'IDLE',
            thumbnail: data.thumbnail,
            frames: data.frames,
            duration: data.duration,
            extractionProgress: 100
          });
        } catch (err: any) {
          console.error('Frame extraction failed:', err);
          updateVideoStatus(id, {
            status: 'ERROR',
            error: 'Failed to process video: ' + err.message
          });
        }
      }
    },
    [currentCount, onUpload, updateVideoStatus, onError]
  );

  return {
    processFiles,
    isDragActive,
    setIsDragActive
  };
};
